import React, { useEffect, useMemo, useState } from 'react';
import styles from './ImagePickerModal.module.scss';
import {
    ImageDetailDto,
    ImageListItemDto,
    ImageUploadInitRequestDto
} from '../../../../types/admin.types';
import {
    completeAdminImageUpload,
    createAdminImageUpload,
    fetchAdminImage,
    fetchAdminImagesPaged,
    uploadToPresignedUrl
} from '../../../../services/admin.service';
import {
    formatBytes,
    getVariantLabel,
    getVariantOrder,
    getVariantUrl,
    IMAGE_STATUS_LABELS,
    mapImageDetail,
    mapImageListItem,
    normalizeVariantType
} from '../../../../utils/image.utils';

export interface ImagePickerSelection {
    id: string;
    url: string;
    thumbnailUrl?: string | null;
    variantType: number | string;
    title?: string | null;
}

interface ImagePickerModalProps {
    isOpen: boolean;
    token?: string | null;
    onClose: () => void;
    onSelect: (selection: ImagePickerSelection) => void;
    preferredVariantType?: number | null;
    title?: string;
    enableUpload?: boolean;
}

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

const ImagePickerModal = ({
    isOpen,
    token,
    onClose,
    onSelect,
    preferredVariantType,
    title = 'Select Image',
    enableUpload = true
}: ImagePickerModalProps) => {
    const [listData, setListData] = useState<ImageListItemDto[]>([]);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize] = useState(24);
    const [totalPages, setTotalPages] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [includeArchived, setIncludeArchived] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [selectedDetail, setSelectedDetail] = useState<ImageDetailDto | null>(null);
    const [selectedVariantType, setSelectedVariantType] = useState<number | null>(null);

    const [activeTab, setActiveTab] = useState<'library' | 'upload'>('library');
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadTitle, setUploadTitle] = useState('');
    const [uploadAltText, setUploadAltText] = useState('');
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadBusy, setUploadBusy] = useState(false);

    const loadImages = async (page: number, query: string, includeArchivedValue: boolean) => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await fetchAdminImagesPaged(page, pageSize, query, includeArchivedValue, token);
            const results = response?.Results ?? [];
            const mapped = results.map(mapImageListItem);
            setListData(mapped);
            setTotalPages(response?.TotalPages ?? 1);
        } catch (error) {
            setErrorMessage('Unable to load images.');
        } finally {
            setIsLoading(false);
        }
    };

    const loadDetail = async (imageId: string) => {
        try {
            const detailResponse = await fetchAdminImage(imageId, token);
            setSelectedDetail(mapImageDetail(detailResponse));
        } catch (error) {
            setErrorMessage('Unable to load image details.');
        }
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        loadImages(pageIndex, appliedSearch, includeArchived);
    }, [isOpen, pageIndex, appliedSearch, includeArchived]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen]);

    useEffect(() => {
        if (!selectedId) {
            setSelectedDetail(null);
            return;
        }
        void loadDetail(selectedId);
    }, [selectedId]);

    useEffect(() => {
        if (!selectedDetail) {
            setSelectedVariantType(null);
            return;
        }

        const variantsWithUrls = (selectedDetail.variants ?? []).filter((variant) => Boolean(variant.url));
        if (variantsWithUrls.length === 0) {
            setSelectedVariantType(null);
            return;
        }

        const preferred = preferredVariantType != null
            ? variantsWithUrls.find((variant) => normalizeVariantType(variant.variantType) === preferredVariantType)
            : null;
        const nextVariant = preferred ?? variantsWithUrls[0];
        setSelectedVariantType(normalizeVariantType(nextVariant.variantType));
    }, [selectedDetail, preferredVariantType]);

    const filteredResults = useMemo(() => {
        if (statusFilter === 'all') {
            return listData;
        }

        const target = Number(statusFilter);
        if (Number.isNaN(target)) {
            return listData;
        }

        return listData.filter((item) => Number(item.status) === target);
    }, [listData, statusFilter]);

    const orderedVariants = useMemo(() => {
        if (!selectedDetail) {
            return [];
        }
        return [...(selectedDetail.variants ?? [])]
            .filter((variant) => Boolean(variant.url))
            .sort((a, b) => getVariantOrder(a.variantType) - getVariantOrder(b.variantType));
    }, [selectedDetail]);

    const selectedVariant = useMemo(() => {
        if (!selectedDetail || selectedVariantType == null) {
            return null;
        }

        return orderedVariants.find((variant) => normalizeVariantType(variant.variantType) === selectedVariantType) ?? null;
    }, [orderedVariants, selectedDetail, selectedVariantType]);

    const handleSelect = () => {
        if (!selectedDetail || !selectedVariant?.url) {
            return;
        }

        const thumbnailUrl = getVariantUrl(selectedDetail.variants, 4);
        onSelect({
            id: selectedDetail.id,
            url: selectedVariant.url,
            thumbnailUrl,
            variantType: selectedVariant.variantType,
            title: selectedDetail.title ?? selectedDetail.originalFileName
        });
        onClose();
    };

    const handleUpload = async () => {
        if (!uploadFile) {
            setUploadError('Choose an image to upload.');
            return;
        }
        if (uploadFile.size > MAX_UPLOAD_BYTES) {
            setUploadError('Image exceeds the 25MB size limit.');
            return;
        }

        setUploadError(null);
        setUploadBusy(true);
        try {
            const initPayload: ImageUploadInitRequestDto = {
                filename: uploadFile.name,
                fileSize: uploadFile.size,
                contentType: uploadFile.type || 'image/jpeg',
                title: uploadTitle.trim().length > 0 ? uploadTitle.trim() : null,
                altText: uploadAltText.trim().length > 0 ? uploadAltText.trim() : null
            };

            const initResponse = await createAdminImageUpload(initPayload, token);
            await uploadToPresignedUrl(initResponse.presignedUrl, uploadFile, { 'x-amz-acl': 'public-read' });
            const completed = await completeAdminImageUpload(initResponse.imageId, token);
            const mapped = mapImageDetail(completed);
            setUploadFile(null);
            setUploadTitle('');
            setUploadAltText('');
            setActiveTab('library');
            setSelectedId(mapped.id);
            setSelectedDetail(mapped);
            await loadImages(pageIndex, appliedSearch, includeArchived);
        } catch (error: any) {
            setUploadError(error?.message || 'Unable to upload image.');
        } finally {
            setUploadBusy(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="image-picker-title">
            <div className={styles.backdrop} onClick={onClose} />
            <div className={styles.modal}>
                <header className={styles.header}>
                    <div>
                        <p className={styles.eyebrow}>Media Library</p>
                        <h2 id="image-picker-title">{title}</h2>
                    </div>
                    <button type="button" className={styles.close} onClick={onClose}>Close</button>
                </header>

                <div className={styles.tabs}>
                    <button
                        type="button"
                        className={activeTab === 'library' ? styles.activeTab : styles.tab}
                        onClick={() => setActiveTab('library')}
                    >
                        Library
                    </button>
                    {enableUpload ? (
                        <button
                            type="button"
                            className={activeTab === 'upload' ? styles.activeTab : styles.tab}
                            onClick={() => setActiveTab('upload')}
                        >
                            Upload
                        </button>
                    ) : null}
                </div>

                {activeTab === 'library' ? (
                    <div className={styles.body}>
                        <div className={styles.searchRow}>
                            <input
                                className={styles.searchInput}
                                placeholder="Search by title, alt text, or filename"
                                value={searchInput}
                                onChange={(event) => setSearchInput(event.target.value)}
                            />
                            <button
                                type="button"
                                className={styles.secondary}
                                onClick={() => {
                                    setAppliedSearch(searchInput.trim());
                                    setPageIndex(1);
                                }}
                            >
                                Search
                            </button>
                            <select
                                className={styles.select}
                                value={statusFilter}
                                onChange={(event) => setStatusFilter(event.target.value)}
                            >
                                <option value="all">All statuses</option>
                                <option value="0">Pending</option>
                                <option value="1">Processing</option>
                                <option value="2">Ready</option>
                                <option value="3">Failed</option>
                            </select>
                            <label className={styles.checkbox}>
                                <input
                                    type="checkbox"
                                    checked={includeArchived}
                                    onChange={(event) => {
                                        setIncludeArchived(event.target.checked);
                                        setPageIndex(1);
                                    }}
                                />
                                Include archived
                            </label>
                        </div>

                        {errorMessage ? <div className={styles.error}>{errorMessage}</div> : null}

                        <div className={styles.content}>
                            <section className={styles.listPanel}>
                                {isLoading ? (
                                    <div className={styles.muted}>Loading images...</div>
                                ) : filteredResults.length === 0 ? (
                                    <div className={styles.muted}>No images found.</div>
                                ) : (
                                    <div className={styles.grid}>
                                        {filteredResults.map((item) => (
                                            <button
                                                type="button"
                                                key={item.id}
                                                className={item.id === selectedId ? styles.cardSelected : styles.card}
                                                onClick={() => setSelectedId(item.id)}
                                            >
                                                <div className={styles.thumbnailWrap}>
                                                    {item.thumbnailUrl ? (
                                                        <img src={item.thumbnailUrl} alt={item.title ?? item.originalFileName} />
                                                    ) : (
                                                        <div className={styles.thumbnailPlaceholder}>No preview</div>
                                                    )}
                                                </div>
                                                <div className={styles.cardBody}>
                                                    <strong>{item.title ?? item.originalFileName}</strong>
                                                    <span className={styles.meta}>{item.width}x{item.height}px</span>
                                                    <span className={styles.status}>{IMAGE_STATUS_LABELS[Number(item.status)] ?? 'Unknown'}</span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <div className={styles.pagination}>
                                    <button
                                        type="button"
                                        className={styles.secondary}
                                        disabled={pageIndex <= 1}
                                        onClick={() => setPageIndex((value) => Math.max(1, value - 1))}
                                    >
                                        Prev
                                    </button>
                                    <span className={styles.pageInfo}>Page {pageIndex} of {totalPages}</span>
                                    <button
                                        type="button"
                                        className={styles.secondary}
                                        disabled={pageIndex >= totalPages}
                                        onClick={() => setPageIndex((value) => Math.min(totalPages, value + 1))}
                                    >
                                        Next
                                    </button>
                                </div>
                            </section>

                            <section className={styles.detailPanel}>
                                {!selectedDetail ? (
                                    <div className={styles.muted}>Select an image to preview details.</div>
                                ) : (
                                    <>
                                        <div className={styles.preview}>
                                            {selectedVariant?.url ? (
                                                <img src={selectedVariant.url} alt={selectedDetail.altText ?? selectedDetail.title ?? 'Selected image'} />
                                            ) : (
                                                <div className={styles.previewPlaceholder}>Variant not available</div>
                                            )}
                                        </div>
                                        <div className={styles.detailMeta}>
                                            <div>
                                                <strong>{selectedDetail.title ?? selectedDetail.originalFileName}</strong>
                                                <div className={styles.meta}>{selectedDetail.width}x{selectedDetail.height}px - {IMAGE_STATUS_LABELS[Number(selectedDetail.status)] ?? 'Unknown'}</div>
                                            </div>
                                            <div className={styles.fieldRow}>
                                                <label htmlFor="variant-select">Variant</label>
                                                <select
                                                    id="variant-select"
                                                    className={styles.select}
                                                    value={selectedVariantType ?? ''}
                                                    onChange={(event) => setSelectedVariantType(Number(event.target.value))}
                                                >
                                                    {orderedVariants.map((variant) => {
                                                        const normalized = normalizeVariantType(variant.variantType);
                                                        return (
                                                            <option key={variant.url ?? String(normalized)} value={normalized ?? ''}>
                                                                {getVariantLabel(variant.variantType)} - {variant.width}x{variant.height} - {formatBytes(variant.fileSizeBytes)}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                            <div className={styles.actions}>
                                                <button
                                                    type="button"
                                                    className={styles.primary}
                                                    disabled={!selectedVariant?.url}
                                                    onClick={handleSelect}
                                                >
                                                    Use Image
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </section>
                        </div>
                    </div>
                ) : (
                    <div className={styles.body}>
                        <div className={styles.uploadPanel}>
                            <div className={styles.fieldRow}>
                                <label htmlFor="image-upload-file">Image file</label>
                                <input
                                    id="image-upload-file"
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                                    onChange={(event) => {
                                        setUploadFile(event.target.files?.[0] ?? null);
                                        setUploadError(null);
                                    }}
                                />
                            </div>
                            <div className={styles.fieldRow}>
                                <label htmlFor="image-upload-title">Title (optional)</label>
                                <input
                                    id="image-upload-title"
                                    value={uploadTitle}
                                    onChange={(event) => setUploadTitle(event.target.value)}
                                    maxLength={200}
                                />
                            </div>
                            <div className={styles.fieldRow}>
                                <label htmlFor="image-upload-alt">Alt text (optional)</label>
                                <input
                                    id="image-upload-alt"
                                    value={uploadAltText}
                                    onChange={(event) => setUploadAltText(event.target.value)}
                                    maxLength={200}
                                />
                            </div>
                            {uploadError ? <div className={styles.error}>{uploadError}</div> : null}
                            <div className={styles.actions}>
                                <button
                                    type="button"
                                    className={styles.primary}
                                    onClick={handleUpload}
                                    disabled={uploadBusy}
                                >
                                    {uploadBusy ? 'Uploading...' : 'Upload Image'}
                                </button>
                                <button type="button" className={styles.secondary} onClick={() => setActiveTab('library')}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImagePickerModal;
